import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Search, Printer, Type, Contrast, ChevronDown, Clock, ShieldAlert,
  Trophy, Award, Sparkles, CheckCircle2, CircleDot, Circle, ArrowRight, Loader2,
  User, Phone, HeartPulse, Scale, Landmark, Umbrella, Home, Laptop, Users, CalendarClock,
  Share2, History, FileArchive, Check, Moon, Sun, Volume2, VolumeX, MinusCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ScoreRing from '../components/readiness/ScoreRing';
import Confetti from '../components/readiness/Confetti';
import ReadinessItemDialog from '../components/readiness/ReadinessItemDialog';
import ShareDialog from '../components/readiness/ShareDialog';
import ActivityLog from '../components/readiness/ActivityLog';
import ExportDialog from '../components/readiness/ExportDialog';
import VoiceNav from '../components/readiness/VoiceNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_ICON = {
  essentials: ShieldAlert, insurance: Umbrella, property: Home, legacy: Sparkles,
};
const STATUS_ICON = {
  not_started: { Icon: Circle, cls: 'text-[#C4BDB4]' },
  in_progress: { Icon: CircleDot, cls: 'text-[#E9B949]' },
  completed: { Icon: CheckCircle2, cls: 'text-[#7FA98C]' },
  not_applicable: { Icon: MinusCircle, cls: 'text-[#C4BDB4]' },
};
const GRADE_COLOR = (g) => (['A+', 'A'].includes(g) ? '#7FA98C' : ['B', 'C'].includes(g) ? '#4F86C6' : '#E07A5F');

export default function FamilyReadiness() {
  const token = localStorage.getItem('session_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recs, setRecs] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [senior, setSenior] = useState(localStorage.getItem('readiness_senior') === '1');
  const [contrast, setContrast] = useState(localStorage.getItem('readiness_contrast') === '1');
  const [dark, setDark] = useState(localStorage.getItem('readiness_dark') === '1');
  const [srMode, setSrMode] = useState(localStorage.getItem('readiness_sr') === '1');
  const navigate = useNavigate();
  const [confettiKey, setConfettiKey] = useState(0);
  const prevBadges = useRef(null);

  // Sharing / multi-checklist context
  const [owner, setOwner] = useState(null); // null = my own checklist
  const [role, setRole] = useState('owner');
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reminders, setReminders] = useState([]);

  const ownerParams = owner ? { owner } : undefined;
  const canEdit = ['owner', 'admin', 'edit'].includes(role);
  const canComment = ['owner', 'admin', 'edit', 'comment'].includes(role);
  const canManage = ['owner', 'admin'].includes(role);
  const isOwnerView = role === 'owner';

  const loadAll = async (announce = false) => {
    try {
      const cfg = { ...auth, params: ownerParams };
      const [c, s, r] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/readiness/checklist`, cfg),
        axios.get(`${BACKEND_URL}/api/readiness/summary`, cfg),
        axios.get(`${BACKEND_URL}/api/readiness/recommendations`, cfg),
      ]);
      setData(c.data); setSummary(s.data); setRecs(r.data);
      setRole(c.data.role || 'owner');
      axios.get(`${BACKEND_URL}/api/readiness/reminders`, cfg)
        .then((rm) => setReminders(rm.data.reminders || [])).catch(() => {});

      const earnedNow = (s.data.badges || []).filter((b) => b.earned).map((b) => b.id);
      if (announce && prevBadges.current) {
        const fresh = earnedNow.filter((id) => !prevBadges.current.includes(id));
        if (fresh.length) {
          const b = s.data.badges.find((x) => x.id === fresh[0]);
          setConfettiKey((k) => k + 1);
          toast.success(`🏆 Badge unlocked: ${b?.name || 'New badge'}!`, { duration: 5000 });
        }
      }
      prevBadges.current = earnedNow;
    } catch {
      toast.error('Could not load your checklist.');
    } finally {
      setLoading(false);
    }
  };

  const setItemApplicable = async (id, applicable) => {
    try {
      const action = applicable ? 'restore' : 'dismiss';
      await axios.post(`${BACKEND_URL}/api/readiness/${id}/${action}`, {}, { ...auth, params: ownerParams });
      toast.success(applicable ? 'Task restored to your checklist.' : 'Task marked as not applicable.');
      setOpenId(null);
      await loadAll(true);
    } catch {
      toast.error('Could not update the task. Please try again.');
    }
  };

  useEffect(() => {
    setLoading(true);
    prevBadges.current = null;
    loadAll();
    if (!owner) {
      axios.get(`${BACKEND_URL}/api/beneficiaries`, auth).then((r) => setBeneficiaries(r.data || [])).catch(() => {});
    } else {
      setBeneficiaries([]);
    }
  }, [owner]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/readiness/shared-with-me`, auth)
      .then((r) => setSharedWithMe(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  // Full-text search (titles, notes, comments, file names, OCR'd document text)
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      axios.get(`${BACKEND_URL}/api/readiness/search`, { ...auth, params: { q, ...(owner ? { owner } : {}) } })
        .then((r) => setSearchResults(r.data.results || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search, owner]); // eslint-disable-line

  // Senior mode scales the whole document (rem-based); revert on unmount/disable.
  useEffect(() => {
    document.documentElement.style.fontSize = senior ? '18px' : '';
    localStorage.setItem('readiness_senior', senior ? '1' : '0');
    return () => { document.documentElement.style.fontSize = ''; };
  }, [senior]);
  useEffect(() => { localStorage.setItem('readiness_contrast', contrast ? '1' : '0'); }, [contrast]);
  useEffect(() => { localStorage.setItem('readiness_dark', dark ? '1' : '0'); }, [dark]);
  useEffect(() => { localStorage.setItem('readiness_sr', srMode ? '1' : '0'); }, [srMode]);

  const speak = (text) => {
    if (!('speechSynthesis' in window) || !text) return;
    try {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    } catch { /* noop */ }
  };

  const scoreSentence = () => summary
    ? `Your family readiness score is ${summary.score} out of 100, grade ${summary.grade}, ${summary.level}. `
      + `You've completed ${summary.completed} of ${summary.total_items} tasks, including ${summary.critical_completed} of ${summary.critical_total} critical tasks.`
    : '';

  const readScore = () => { const s = scoreSentence(); speak(s); toast('Your readiness', { description: s }); };

  // Announce the score once when screen-reader mode is turned on.
  const srAnnounced = useRef(false);
  useEffect(() => {
    if (srMode && summary && !srAnnounced.current) { speak(scoreSentence()); srAnnounced.current = true; }
    if (!srMode) srAnnounced.current = false;
  }, [srMode, summary]); // eslint-disable-line

  const itemsByCat = useMemo(() => {
    if (!data) return {};
    const map = {};
    data.items.forEach((it) => { (map[it.category] = map[it.category] || []).push(it); });
    return map;
  }, [data]);

  const isSearching = search.trim().length >= 2;

  const openItem = data?.items.find((i) => i.id === openId) || null;

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4F86C6]" /></div>;
  }

  return (
    <div className={`min-h-screen ${dark ? 'bg-[#0F151C]' : 'bg-white'} text-[#2C3A47] ${contrast ? 'contrast-[1.15]' : ''}`} data-testid="readiness-page">
      <Confetti fireKey={confettiKey} />
      <div aria-live="polite" className="sr-only" data-testid="readiness-live-region">{srMode && summary ? scoreSentence() : ''}</div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#EEECE8] print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#57534E] hover:text-[#4F86C6]" data-testid="readiness-back">
            <ArrowLeft className="h-5 w-5" /> <span className="hidden sm:inline font-medium">Dashboard</span>
          </Link>
          <div className="relative flex-1 min-w-0 flex justify-center">
            <button
              onClick={() => sharedWithMe.length > 0 && setSwitcherOpen((o) => !o)}
              data-testid="readiness-checklist-switcher"
              className={`flex items-center gap-1.5 font-bold text-lg sm:text-xl text-[#2C3A47] max-w-full ${sharedWithMe.length > 0 ? 'hover:text-[#4F86C6]' : 'cursor-default'}`}>
              <span className="truncate">{owner ? `${sharedWithMe.find((x) => x.owner_user_id === owner)?.owner_name || 'Shared'}'s Checklist` : 'Family Readiness Checklist'}</span>
              {sharedWithMe.length > 0 && <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} />}
            </button>
            {switcherOpen && (
              <div className="absolute top-11 z-50 w-72 bg-white rounded-2xl shadow-xl border border-[#EEECE8] p-2" data-testid="readiness-switcher-menu">
                <button onClick={() => { setOwner(null); setSwitcherOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm hover:bg-[#F5F4F2] ${!owner ? 'bg-[#F1F6FB] text-[#3A6EA5] font-semibold' : 'text-[#57534E]'}`}>
                  My Checklist
                </button>
                <div className="text-xs text-[#9C948B] px-3 pt-2 pb-1 font-semibold uppercase tracking-wide">Shared with me</div>
                {sharedWithMe.map((s) => (
                  <button key={s.owner_user_id} onClick={() => { setOwner(s.owner_user_id); setSwitcherOpen(false); }}
                    data-testid={`readiness-switch-${s.owner_user_id}`}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm hover:bg-[#F5F4F2] ${owner === s.owner_user_id ? 'bg-[#F1F6FB] text-[#3A6EA5] font-semibold' : 'text-[#57534E]'}`}>
                    <span className="truncate">{s.owner_name}</span>
                    <span className="text-xs text-[#8D8880] capitalize shrink-0">{s.role} · {s.score}/100</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {canComment && (
              <button onClick={() => setShowShare(true)} data-testid="readiness-share-button" title="Share"
                className={`p-2 rounded-full text-[#8D8880] hover:bg-[#F5F4F2] ${!isOwnerView ? 'hidden' : ''}`}><Share2 className="h-5 w-5" /></button>
            )}
            {canManage && (
              <button onClick={() => setShowActivity(true)} data-testid="readiness-activity-button" title="Activity log"
                className="p-2 rounded-full text-[#8D8880] hover:bg-[#F5F4F2]"><History className="h-5 w-5" /></button>
            )}
            {canManage && (
              <button onClick={() => setShowExport(true)} data-testid="readiness-export-button" title="Export for executor"
                className="p-2 rounded-full text-[#8D8880] hover:bg-[#F5F4F2]"><FileArchive className="h-5 w-5" /></button>
            )}
            <button onClick={() => setSenior(!senior)} data-testid="readiness-toggle-senior" title="Large text" aria-label="Toggle large text"
              className={`p-2 rounded-full ${senior ? 'bg-[#E8F0F9] text-[#3A6EA5]' : 'text-[#8D8880] hover:bg-[#F5F4F2]'}`}><Type className="h-5 w-5" /></button>
            <button onClick={() => setContrast(!contrast)} data-testid="readiness-toggle-contrast" title="High contrast" aria-label="Toggle high contrast"
              className={`p-2 rounded-full ${contrast ? 'bg-[#E8F0F9] text-[#3A6EA5]' : 'text-[#8D8880] hover:bg-[#F5F4F2]'}`}><Contrast className="h-5 w-5" /></button>
            <button onClick={() => setDark(!dark)} data-testid="readiness-toggle-dark" title="Dark mode" aria-label="Toggle dark mode"
              className={`p-2 rounded-full ${dark ? 'bg-[#E8F0F9] text-[#3A6EA5]' : 'text-[#8D8880] hover:bg-[#F5F4F2]'}`}>{dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</button>
            <button onClick={() => { setSrMode(!srMode); if (!srMode) readScore(); else window.speechSynthesis?.cancel(); }}
              data-testid="readiness-toggle-sr" title="Screen-reader mode (read aloud)" aria-label="Toggle screen reader mode"
              className={`p-2 rounded-full ${srMode ? 'bg-[#E8F0F9] text-[#3A6EA5]' : 'text-[#8D8880] hover:bg-[#F5F4F2]'}`}>{srMode ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}</button>
            <VoiceNav
              categories={data?.categories || []}
              onSearch={(q) => setSearch(q)}
              onReadScore={readScore}
              onOpenCategory={(id) => { setExpanded((e) => ({ ...e, [id]: true })); setSearch(''); document.querySelector(`[data-testid="readiness-category-${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              onGoDashboard={() => navigate('/dashboard')}
              speak={speak}
            />
            <button onClick={() => window.print()} data-testid="readiness-print" title="Print / Save PDF" aria-label="Print or save as PDF"
              className="p-2 rounded-full text-[#8D8880] hover:bg-[#F5F4F2]"><Printer className="h-5 w-5" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {owner && (
          <div className="flex items-center gap-2 bg-[#F1F6FB] border border-[#E1EAF3] rounded-2xl px-4 py-3 text-sm text-[#3A6EA5]" data-testid="readiness-shared-banner">
            <Users className="h-4 w-4" />
            Viewing <span className="font-semibold">{sharedWithMe.find((x) => x.owner_user_id === owner)?.owner_name || 'a family member'}'s</span> checklist —
            your role: <span className="font-semibold capitalize">{role}</span>
            {!canEdit && ' (read-only)'}
          </div>
        )}
        {reminders.length > 0 && (
          <section data-testid="readiness-reminders" className="rounded-2xl border border-[#F3D9A4] bg-[#FCF6E9] p-4 sm:p-5">
            <p className="text-sm font-bold text-[#9A7B1F] mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Attention needed</p>
            <div className="space-y-2">
              {reminders.map((rm, i) => {
                const expired = rm.days_until < 0;
                const when = expired
                  ? `${rm.label.toLowerCase()} expired ${Math.abs(rm.days_until)} day${Math.abs(rm.days_until) === 1 ? '' : 's'} ago`
                  : rm.days_until === 0 ? `${rm.label.toLowerCase()} is today`
                  : `${rm.label.toLowerCase()} in ${rm.days_until} day${rm.days_until === 1 ? '' : 's'}`;
                const dot = expired ? 'bg-[#C0492E]' : rm.severity === 'warning' ? 'bg-[#E9B949]' : 'bg-[#4F86C6]';
                return (
                  <button key={i} onClick={() => setOpenId(rm.item_id)} data-testid={`readiness-reminder-${rm.item_id}`}
                    className="w-full flex items-center gap-3 text-left bg-white/70 hover:bg-white rounded-xl px-3 py-2.5 transition-colors">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                    <span className="flex-1 min-w-0 text-sm text-[#4A5560]"><span className="font-semibold text-[#2C3A47]">{rm.title}</span> — {when} <span className="text-[#9C948B]">({rm.date})</span></span>
                    <ArrowRight className="h-4 w-4 text-[#CBB26A] shrink-0" />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Dashboard */}
        <section className="grid lg:grid-cols-[auto,1fr] gap-8 items-center bg-gradient-to-br from-[#F7FAFD] to-[#F2F7F3] rounded-3xl p-6 sm:p-8 border border-[#E8EEF2]">
          <div className="flex flex-col items-center" data-testid="readiness-score">
            <ScoreRing value={summary.score} size={190} color={GRADE_COLOR(summary.grade)}>
              <span className="text-5xl font-bold" style={{ color: GRADE_COLOR(summary.grade) }}>{summary.score}</span>
              <span className="text-sm text-[#8D8880]">/ 100</span>
            </ScoreRing>
            <div className="mt-3 text-center">
              <div className="inline-flex items-center gap-2">
                <span className="text-2xl font-bold" style={{ color: GRADE_COLOR(summary.grade) }}>Grade {summary.grade}</span>
              </div>
              <p className="text-sm font-medium text-[#57534E] mt-1">{summary.level}</p>
            </div>
          </div>

          <div className="space-y-5 w-full">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat icon={ShieldAlert} label="Critical done" value={`${summary.critical_completed}/${summary.critical_total}`} />
              <Stat icon={CheckCircle2} label="Categories done" value={`${summary.categories_completed}/${summary.categories_total}`} />
              <Stat icon={Circle} label="Tasks left" value={summary.tasks_remaining} />
              <Stat icon={Clock} label="Time left" value={`~${Math.round(summary.estimated_minutes_remaining / 60)}h`} />
              <Stat icon={CheckCircle2} label="Completed" value={`${summary.completed}/${summary.total_items}`} />
              <Stat icon={Trophy} label="Grade" value={summary.grade} />
            </div>

            {recs?.recommendations?.length > 0 && (
              <div className="bg-white rounded-2xl p-4 border border-[#E8EEF2]" data-testid="readiness-recommendations">
                <p className="text-sm font-semibold text-[#3A6EA5] mb-2 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#E9B949]" /> {recs.headline}</p>
                <div className="flex flex-wrap gap-2">
                  {recs.recommendations.slice(0, 4).map((r) => (
                    <button key={r.id} onClick={() => setOpenId(r.id)}
                      className="text-sm px-3 py-1.5 rounded-full bg-[#F1F6FB] text-[#3A6EA5] hover:bg-[#E1EAF3] transition-colors inline-flex items-center gap-1">
                      {r.title} <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Badges */}
        {summary.badges?.length > 0 && (
          <section>
            <h2 className={`text-lg font-bold mb-3 flex items-center gap-2 ${dark ? 'text-[#E8EEF2]' : 'text-[#2C3A47]'}`}><Trophy className="h-5 w-5 text-[#E9B949]" /> Achievements</h2>
            <div className="flex flex-wrap gap-3">
              {summary.badges.map((b) => (
                <div key={b.id} data-testid={`readiness-badge-${b.id}`}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border ${b.earned ? 'bg-[#F2F7F3] border-[#CDE0D2]' : 'bg-[#F7F6F4] border-[#EEECE8] opacity-60'}`}>
                  <Award className={`h-5 w-5 ${b.earned ? 'text-[#7FA98C]' : 'text-[#C4BDB4]'}`} />
                  <div>
                    <p className="text-sm font-semibold text-[#2C3A47]">{b.name}</p>
                    <p className="text-xs text-[#8D8880]">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Search */}
        <div className="relative max-w-md print:hidden">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A29E]" />
          <Input data-testid="readiness-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks, notes & inside your documents…" className="pl-10 rounded-full border-[#EAE7E2] h-11" />
        </div>

        {/* Search results */}
        {isSearching && (
          <section className="space-y-3" data-testid="readiness-search-results">
            <p className="text-sm text-[#8D8880]">
              {searching ? 'Searching…' : `${searchResults.length} result${searchResults.length === 1 ? '' : 's'} for "${search.trim()}"`}
            </p>
            {!searching && searchResults.length === 0 && (
              <p className="text-sm text-[#9C948B] py-8 text-center">No matches in titles, notes, comments, or document text.</p>
            )}
            {searchResults.map((r) => {
              const catName = data.categories.find((c) => c.id === r.category)?.name || r.category;
              const St = STATUS_ICON[r.status] || STATUS_ICON.not_started;
              return (
                <button key={r.item_id} onClick={() => setOpenId(r.item_id)} data-testid={`readiness-search-item-${r.item_id}`}
                  className="w-full text-left bg-white rounded-2xl border border-[#EEECE8] hover:border-[#4F86C6] p-4 transition-colors">
                  <div className="flex items-center gap-3">
                    <St.Icon className={`h-5 w-5 shrink-0 ${St.cls}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#2C3A47]">{r.title}</p>
                      <p className="text-xs text-[#8D8880]">{catName}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-[#CBD5E1]" />
                  </div>
                  {r.snippet && <p className="text-sm text-[#4A5560] mt-2 line-clamp-2 whitespace-pre-wrap">{r.snippet}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.matched_in.map((m, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#F1F6FB] text-[#3A6EA5]">Found in {m}</span>
                    ))}
                  </div>
                </button>
              );
            })}
          </section>
        )}

        {/* Categories */}
        {!isSearching && (
        <section className="space-y-4">
          {data.categories.map((cat) => {
            const items = itemsByCat[cat.id] || [];
            const cp = summary.category_progress.find((c) => c.id === cat.id);
            const Icon = CAT_ICON[cat.id] || Sparkles;
            const isOpen = expanded[cat.id] ?? false;
            return (
              <div key={cat.id} className="bg-white rounded-3xl border border-[#EEECE8] overflow-hidden" data-testid={`readiness-category-${cat.id}`}>
                <button onClick={() => setExpanded((e) => ({ ...e, [cat.id]: !isOpen }))}
                  className="w-full flex items-center gap-4 p-5 hover:bg-[#FAFBFC] transition-colors text-left">
                  <div className="w-12 h-12 rounded-2xl bg-[#F1F6FB] flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-[#4F86C6]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#2C3A47]">{cat.name}</h3>
                    <p className="text-sm text-[#8D8880]">{cp?.completed || 0} of {cp?.total || 0} complete</p>
                  </div>
                  <ScoreRing value={cp?.percent || 0} size={48} stroke={5} color="#7FA98C">
                    <span className="text-xs font-bold text-[#57534E]">{cp?.percent || 0}%</span>
                  </ScoreRing>
                  <ChevronDown className={`h-5 w-5 text-[#A8A29E] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="px-5 pb-5 divide-y divide-[#F2F1EE]">
                        {items.map((it) => {
                          const St = STATUS_ICON[it.status] || STATUS_ICON.not_started;
                          const na = it.status === 'not_applicable';
                          return (
                            <button key={it.id} onClick={() => setOpenId(it.id)} data-testid={`readiness-item-${it.id}`}
                              className={`w-full flex items-center gap-3 py-3 text-left hover:bg-[#FAFBFC] rounded-xl px-2 -mx-2 transition-colors ${na ? 'opacity-55' : ''}`}>
                              <St.Icon className={`h-6 w-6 shrink-0 ${St.cls}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-[#2C3A47] truncate ${na ? 'line-through' : ''}`}>{it.title}</p>
                                {na ? (
                                  <p className="text-xs text-[#A8A29E]">Not applicable — doesn't count toward your score</p>
                                ) : (
                                  <p className="text-xs text-[#A8A29E] flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{it.estimated_minutes}m</span>
                                    {it.tier === 'critical' && <span className="text-[#C0492E] font-semibold">Critical</span>}
                                    <span>· in {it.section_label}</span>
                                  </p>
                                )}
                              </div>
                              <ArrowRight className="h-4 w-4 text-[#CBD5E1] shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </section>
        )}

        {/* Final summary */}
        <section className="rounded-3xl bg-gradient-to-br from-[#4F86C6] to-[#3A6EA5] text-white text-center p-8 sm:p-10" data-testid="readiness-final">
          <p className="text-white/80 text-sm uppercase tracking-wider font-semibold mb-2">Your Family Readiness</p>
          <h2 className="text-3xl font-bold">You've completed {summary.completed} of {summary.total_items} tasks</h2>
          <p className="mt-2 text-white/90 text-lg">Preparedness Score: <span className="font-bold">{summary.score}/100</span> · Grade {summary.grade} · {summary.level}</p>
          <p className="mt-4 text-white/85 max-w-xl mx-auto">
            {summary.score >= 96
              ? 'Incredible — your family is truly ready. Review yearly to keep everything current.'
              : summary.critical_tasks_remaining > 0
                ? `You have ${summary.critical_tasks_remaining} critical task${summary.critical_tasks_remaining > 1 ? 's' : ''} left. Completing these makes the biggest difference for your loved ones.`
                : 'Great progress! Keep going to give your family complete peace of mind.'}
          </p>
          {recs?.recommendations?.[0] && (
            <Button onClick={() => setOpenId(recs.recommendations[0].id)} className="mt-6 rounded-full bg-white text-[#3A6EA5] hover:bg-white/90 font-semibold px-6">
              Next: {recs.recommendations[0].title} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </section>
      </main>

      <ReadinessItemDialog
        item={openItem}
        open={!!openId}
        onClose={() => setOpenId(null)}
        canEdit={canEdit}
        onSetApplicable={setItemApplicable}
      />

      <ShareDialog open={showShare} onClose={() => setShowShare(false)} />
      <ActivityLog open={showActivity} onClose={() => setShowActivity(false)} owner={owner} />
      <ExportDialog open={showExport} onClose={() => setShowExport(false)} owner={owner} />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl p-3 border border-[#E8EEF2] text-center">
    <Icon className="h-4 w-4 text-[#8DA9C4] mx-auto mb-1" />
    <p className="text-xl font-bold text-[#2C3A47]">{value}</p>
    <p className="text-xs text-[#8D8880]">{label}</p>
  </div>
);
